import { Components } from "@thisgorilla/primate-ds";
import { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";

import { WithHeightTransition } from "../../../HOC/WithHeightTransition";
import { MAX_POLL_OPTIONS } from "../../../utils";
import { FlexColumn, transition, TransitionProps } from "../Styles";
import { PollContentProps } from "./PollContent";
import { PollOption } from "./PollOption";
import { VotedFeedBack } from "./VotedFeedback";

export const PollContent = ({
  isWithinRepostModal,
  pollData,
  onOptionClick,
}: PollContentProps.IProps) => {
  const userVotedChoice = pollData?.user_voted_choice;
  const votes = pollData?.votes;
  const totalVotes = pollData?.total_votes;
  const isPollActive = pollData?.active;
  type Votes = keyof typeof votes;

  const { typography, Color } = useTheme();
  const isVoted = pollData?.poll_interaction_user_voted;

  const [containerHeight, setContainerHeight] = useState(0);

  const pollOptions = useMemo(() => {
    if (!pollData?.id) return [];
    return Array.from({ length: MAX_POLL_OPTIONS }, (_, i) => {
      const optionIndex = i + 1;
      const key = `choice_${optionIndex}` as PollContentProps.ChoiceKeys;
      const title = pollData?.[key] as Votes;

      const percentage =
        (votes?.[title] ?? 0) === 0
          ? 0
          : Math.round((votes[title] / totalVotes) * 100);
      if (!title) return null;
      return (
        <PollOption
          key={key}
          title={`${title}`}
          onClick={() => {
            if (isWithinRepostModal) return;
            if (!isPollActive || isVoted) return;
            onOptionClick(optionIndex);
          }}
          isVoted={isVoted}
          percentage={percentage}
          isPollActive={isPollActive}
          isWithinRepostModal={isWithinRepostModal}
        />
      );
    }).filter(Boolean);
    //eslint-disable-next-line
  }, [pollData, isVoted, votes, isPollActive]);

  if (!pollData || !pollData.id) return null;

  return (
    <Container
      $gapInPx={10}
      $height={containerHeight}
      className="poll-content-container"
    >
      <WithHeightTransition
        additionalHeight={16}
        setContainerHeight={setContainerHeight}
      >
        <Widget $gapInPx={8}>
          {!isWithinRepostModal && isVoted && (
            <VotedFeedBack selectedPollOption={userVotedChoice} />
          )}
          <Components.Text
            text={pollData?.question}
            style={{ tokenStyle: typography.Body.L }}
            fontWeight="font-weight-semibold"
          />
          {pollOptions}
          {!isWithinRepostModal && (isVoted || !isPollActive) && (
            <Components.Text
              text={`${totalVotes} vote${totalVotes > 1 ? "s" : ""} ${
                isPollActive ? "" : "• Poll closed"
              }`}
              style={{ tokenStyle: typography.Body.M }}
              color={Color.text["text-tertiary"].value}
            />
          )}
        </Widget>
      </WithHeightTransition>
    </Container>
  );
};

const Container = styled(FlexColumn)<TransitionProps>`
  ${transition}
`;
const Widget = styled(FlexColumn)(
  ({ theme }) => `
  border-radius: ${theme.radius["radius-xs"].value}rem;
  border: ${theme.border_width["border-width-thin"].value}rem solid ${theme.Color.border["ui-border-neutral"].value};
  padding: ${theme.spacing["spacing-2"].value}rem;
  background-color: ${theme.Color.elevation.surface["surface-secondary"].value};
  background: ${theme.Color.elevation.surface["surface-primary"].value};
`
);
